import {
  createStyles,
  FormControl,
  InputBase,
  InputLabel,
  makeStyles,
  TextField as MuiTextField,
  TextFieldProps,
} from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { get, uniqueId } from 'lodash';
import React, { FC, forwardRef, ReactNode } from 'react';
import { useFormContext } from 'react-hook-form';

const useStyles = makeStyles(() =>
  createStyles({
    noOutline: {
      border: 'none',
    },
  })
);

type Props = {
  editable?: boolean;
  valueLabelMap?: Record<string, string>;
  nonEditableChildren?: ReactNode;
  loading?: boolean;
} & TextFieldProps;

export const TextFieldWithoutRef: FC<Props> = ({
  editable: editableProp,
  valueLabelMap = {},
  nonEditableChildren,
  loading,
  margin = 'dense',
  ...props
}) => {
  const classes = useStyles();
  const { errors = {} } = useFormContext() ?? {};
  const errorMessage = get(errors, `${props.name}.message`, '');

  const editable = editableProp ?? !props.InputProps?.readOnly;

  const id = `${props.name}-input-base-${props.id || uniqueId()}`;

  return editable ? (
    <MuiTextField
      error={Boolean(errorMessage)}
      helperText={errorMessage}
      InputProps={{
        ...props?.InputProps,
        style: {
          minWidth: 203,
          ...props?.InputProps?.style,
        },
      }}
      margin={margin as 'none' | 'dense'}
      {...props}
    />
  ) : (props.children && !props.select) || nonEditableChildren ? (
    <FormControl
      className={margin === 'dense' ? 'MuiFormControl-marginDense' : ''}
    >
      <InputLabel
        shrink
        htmlFor={id}
        required={props.required && editable}
        error={props.error}
      >
        {props.label}
      </InputLabel>
      {/* TODO: fix 'MuiOutlinedInput-input' class won't be applied without rendering MuiTextField  */}
      <MuiTextField style={{ display: 'none' }} />
      {loading ? (
        <Skeleton
          width={props?.InputProps?.style?.width || 203}
          height={53.63}
          className="MuiOutlinedInput-input"
        />
      ) : (props.children && !props.select) || nonEditableChildren ? (
        <div className="MuiInputBase-root MuiInputBase-formControl">
          <div
            className={`MuiOutlinedInput-input ${
              margin === 'dense' ? 'MuiOutlinedInput-inputMarginDense' : ''
            }`}
            style={{ minWidth: 203, ...props?.InputProps?.style }}
          >
            {props.select ? nonEditableChildren : props.children}
          </div>
        </div>
      ) : (
        <InputBase
          id={id}
          value={valueLabelMap[props.value as any] || props.value}
          type={props.type}
          inputProps={{
            'aria-label': props.name,
            readOnly: !editable,
            className: `MuiOutlinedInput-input ${
              margin === 'dense' ? 'MuiOutlinedInput-inputMarginDense' : ''
            }`,
          }}
          style={props?.InputProps?.style}
          multiline={props.multiline}
          minRows={props.minRows}
          maxRows={props.maxRows}
        />
      )}
    </FormControl>
  ) : (
    // @ts-expect-error notchedOutline
    <MuiTextField
      {...props}
      margin={margin as 'none' | 'dense'}
      value={props.value || ''}
      required={props.required && editable}
      InputProps={{
        ...props.InputProps,
        readOnly: !editable,
        style: {
          minWidth: 203,
          ...props?.InputProps?.style,
        },
        classes: {
          notchedOutline: !editable ? classes.noOutline : undefined,
          ...props.InputProps?.classes,
        },
      }}
      SelectProps={{
        IconComponent: () => null,
      }}
    />
  );
};

const TextField = forwardRef<Props['inputRef'], Props>((props, ref) => (
  <TextFieldWithoutRef inputRef={ref} {...props} />
));

export default TextField;
